import React, { useState, useEffect, useRef } from "react";
import { Route, Redirect } from "react-router-dom";
import { Spin } from "antd";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import FeatureErrorBoundary from "../FeatureErrorBoundary";
import db, { isDbBridgeAvailable } from "../../database";
import useSessionStore from "../../stores/useSessionStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useAuthStore from "../../stores/useAuthStore";
import { getTillMode, TILL_MODES } from "../../constants/accessControl";
import { trace } from "../../utils/debugTrace";
import { canReuseOpenTillEvent, getOpenTillRoute, isWaiterUser } from "../../utils/tillOpenGate";
import { isPastBusinessDay } from "../../utils/tillBusinessDate";

// Only routes that read from db.tillEvents for core operations get this guard.
// Add a path here only when that screen breaks without an open till event.
const TILL_EVENT_ROUTES = ["/pos"];

// BUG-2066/2067/2068 diagnostics: this component has no reactive store
// subscription of its own, so every re-render is caused by an ancestor
// re-rendering. Counting + logging every render (not just the redirect
// branch) so it can be correlated by timestamp against useSessionStore's
// StoreDiag.session log (added alongside this) to find what actually
// triggers the re-render that lets a stale-cookie read slip through in the
// gap before handleLogin's window.location.assign() lands. Remove once root
// cause is confirmed.
let renderCount = 0;

// BUG-2066/2067/2068 + BUG-2097: confirmed root cause. This component
// re-renders on a continuous ~1000ms tick unrelated to any auth change
// (proven via the renderCount trace above: 990ms/999ms/1011ms deltas,
// unconditional). Any single tick that lands while cookies are momentarily
// unreadable used to redirect to a bare "/sign-in" instantly:
//   - during the lock flow, handleLogin clears cookies *before* its own
//     window.location.assign() lands -- a tick landing in that gap produced
//     exactly this. CW Browser's checkNav reads that bare URL as a genuine
//     logout (BUG-2066/2067/2068's escalation into a full session teardown).
//   - outside the lock flow (any other momentary cookie-read glitch) it's
//     the same redirect-and-bounce-back, visible as the billing screen
//     blinking (BUG-2097, still reported live on GPR after two unrelated
//     BUG-2097 fixes -- this is the third, previously unaddressed cause).
// Fix: don't trust a single unauthenticated snapshot.
//   - if useSessionStore's `locked` flag is true, a lock-triggered hard
//     navigation is already in flight -- never redirect from here; that
//     navigation will land on its own. LOCK_NAV_GRACE_MS is only a safety
//     net in case it somehow never does (e.g. offline).
//   - otherwise, wait SETTLE_MS before committing, so a single bad tick has
//     a chance to self-correct on the next one before we act on it.
const LOCK_NAV_GRACE_MS = 4000;
const SETTLE_MS = 500;

const PrivateRoute = ({ component: Component, errorBoundary, ...rest }) => {
  renderCount += 1;
  const authHeaders = getOAuthHeaders();
  const cleanToken = authHeaders?.access_token;
  const email = authHeaders?.email;
  const username = authHeaders?.username;

  const isAuthenticated = cleanToken !== "bearer undefined" && (email || username);
  trace("PrivateRoute", "render", { renderCount, path: rest.path, isAuthenticated, pathname: window.location.pathname });

  // See LOCK_NAV_GRACE_MS/SETTLE_MS comment above.
  const unauthSinceRef = useRef(null);
  const unauthTimerRef = useRef(null);
  const [, forceRecheck] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      unauthSinceRef.current = null;
      if (unauthTimerRef.current) {
        clearTimeout(unauthTimerRef.current);
        unauthTimerRef.current = null;
      }
      return;
    }
    if (unauthSinceRef.current === null) {
      unauthSinceRef.current = Date.now();
    }
    if (unauthTimerRef.current) return; // a recheck is already scheduled
    const locked = useSessionStore.getState().locked === true;
    const grace = locked ? LOCK_NAV_GRACE_MS : SETTLE_MS;
    const remaining = Math.max(0, grace - (Date.now() - unauthSinceRef.current));
    unauthTimerRef.current = setTimeout(() => {
      unauthTimerRef.current = null;
      forceRecheck((n) => n + 1);
    }, remaining);
  });

  useEffect(
    () => () => {
      if (unauthTimerRef.current) clearTimeout(unauthTimerRef.current);
    },
    []
  );

  const lockedNow = useSessionStore.getState().locked === true;
  const confirmedUnauthenticated =
    !isAuthenticated && unauthSinceRef.current !== null && Date.now() - unauthSinceRef.current >= (lockedNow ? LOCK_NAV_GRACE_MS : SETTLE_MS);

  const needsTillCheck = isAuthenticated && TILL_EVENT_ROUTES.some((p) => rest.path?.startsWith(p));

  // "checking" → spinner while verifying IndexedDB open event
  // "ok"          → proceed to render normally
  // "/till"       → stale Zustand session, no open event in IndexedDB
  // "/close-till" → open event exists but from a previous business day
  const [tillCheck, setTillCheck] = useState(needsTillCheck ? "checking" : "ok");

  useEffect(() => {
    if (!needsTillCheck) {
      setTillCheck("ok");
      return;
    }

    const verify = async () => {
      try {
        const posConfig = usePOSConfigStore.getState().posConfig;
        const shouldCheckTill = getTillMode(posConfig) === TILL_MODES.NORMAL;

        if (!shouldCheckTill) {
          setTillCheck("ok");
          return;
        }

        const tillSession = useSessionStore.getState().tillSession;
        if (!tillSession || tillSession.tillStatus !== "open") {
          // No active session — PointOfSaleCore handles the /till redirect
          setTillCheck("ok");
          return;
        }

        // Verify the open event actually exists in IndexedDB.
        // Zustand persists across restarts; IndexedDB can diverge (server sync
        // closed the event, DB cleared, tab restored with stale state, etc.).
        // Use toArray() + Array.find() instead of .filter() — closures are lost via IPC in SQLite mode.
        const allSessionEvents = await db.tillEvents
          .where("tillSessionId")
          .equals(tillSession.tillSessionId)
          .toArray();
        const openEvents = allSessionEvents.filter((e) => e.tillStatus === "open");

        if (openEvents.length === 0) {
          if (!isDbBridgeAvailable()) {
            // The Electron IPC bridge is momentarily unreachable (observed after
            // the app resumes from idle) — an empty query result here means
            // "couldn't ask," not "confirmed gone." Leave tillSession alone;
            // the next visibility/focus re-check will re-verify once the
            // bridge is back, instead of bouncing a genuinely open session to
            // /till on a false negative.
            setTillCheck("ok");
            return;
          }
          // Zustand says open but no matching open event in IndexedDB — clear stale state
          useSessionStore.getState().setTillSession(null);
          setTillCheck("/till");
          return;
        }

        // Open event exists — check if it is from a previous business day.
        // BUG-1902: only force /close-till on a date rollover when Enable Auto Close
        // ("Y") is on for this till template. When it's off, billing continues on the
        // same open till across midnight until the user closes it manually.
        // BUG-2171: business date, not opening date — a till opened last evening for
        // today (NXT post-close sales) is not from a previous business day.
        if (isPastBusinessDay(openEvents[0]) && posConfig?.enableAutoStoreClose === "Y") {
          // TSK-4249 follow-up: waiters don't own a till — this guard (like
          // TillModalHandler's) is only for cashiers/managers. Mirrors the
          // matchedRep check in TillModalHandler.js/Till.js/Login/index.js.
          const username = localStorage.getItem("username") || "";
          const salesReps = useAuthStore.getState().tillData?.tillAccess?.csBunit?.salesRep || [];
          const matchedRep = salesReps.find((rep) => rep?.name === username);
          if (!matchedRep) {
            setTillCheck("/close-till");
            return;
          }
        }

        // BUG-2441 C4: on a showTillOpening = "Y" till, an open event without the
        // server's day-open id was opened on the device only — every bill under it
        // saves a NULL cwr_store_dailyops_till_id. Send the cashier to Open Till before
        // billing. The session is cleared first, or Open Till bounces back to /pos.
        const isWaiter = isWaiterUser(useAuthStore.getState().tillData, localStorage.getItem("username"));
        if (!canReuseOpenTillEvent(openEvents[0], posConfig, isWaiter)) {
          const openTillRoute = getOpenTillRoute(posConfig);
          trace("PrivateRoute", "BUG-2441: open till event has no server day-open → " + openTillRoute, {
            tillSessionId: tillSession.tillSessionId,
          });
          useSessionStore.getState().setTillSession(null);
          setTillCheck(openTillRoute);
          return;
        }

        setTillCheck("ok");
      } catch (err) {
        console.error("[PrivateRoute] Till event verification failed:", err);
        // Fail open — never block the user due to a DB read error
        setTillCheck("ok");
      }
    };

    verify();

    // Same gap as TillModalHandler: this only re-runs on mount/route-change.
    // If the app is left open on /pos across a business-date rollover with
    // no navigation or reload, the stale-till redirect is never re-evaluated.
    // Re-check on visibility/focus so foregrounding an already-open tab works too.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        verify();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", verify);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", verify);
    };
  }, [needsTillCheck]);

  if (tillCheck === "checking") {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (tillCheck === "/till" || tillCheck === "/close-till" || tillCheck === "/open-till" || tillCheck === "/open-till-component") {
    return <Route {...rest} render={() => <Redirect to={tillCheck} />} />;
  }

  return (
    <>
      <Route
        {...rest}
        render={(props) =>
          isAuthenticated ? (
            errorBoundary ? (
              <FeatureErrorBoundary name={errorBoundary}>
                <Component {...props} {...rest} />
              </FeatureErrorBoundary>
            ) : (
              <Component {...props} {...rest} />
            )
          ) : confirmedUnauthenticated ? (
            (() => {
              // BUG-2066/2067/2068: this is a second, previously-unlogged path to the
              // same bare "/sign-in" (no readOnly=true) that AuthRedirect produces —
              // if a protected route re-renders after cleanToken/email/username
              // transiently drop out (e.g. a cookie-clear race), this redirect fires
              // instead of AuthRedirect's catch-all, and CW Browser's checkNav reads
              // it the same way: a genuine logout, not just a locked re-auth. Only
              // reached after surviving LOCK_NAV_GRACE_MS/SETTLE_MS, so this is a
              // confirmed logout, not a single bad tick.
              trace("PrivateRoute", "unauthenticated → bare /sign-in", {
                requestedPath: rest.path,
                fromPathname: props.location?.pathname,
                hasCleanToken: !!cleanToken && cleanToken !== "bearer undefined",
                hasEmail: !!email,
                hasUsername: !!username,
              });
              return (
                <Redirect
                  to={{
                    pathname: "/sign-in",
                    state: { from: props.location },
                  }}
                />
              );
            })()
          ) : (
            // Unauthenticated but not yet confirmed (still within the grace window) --
            // render neither the protected Component nor the redirect. A spinner here
            // is a brief, harmless flicker; either the in-flight hard navigation lands
            // and this component unmounts, or the grace period expires and we redirect.
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
              <Spin size="large" />
            </div>
          )
        }
      />
    </>
  );
};

export default PrivateRoute;
